import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { CheckValue } from "../../../../../../Service/service";
import {
  staticVar,
  useActiveCampusContext,
  useActiveDroneNestContext,
  useActiveFloorContext,
} from "../../Constant";
import { setDroneNest } from "../../../../../../Service/mappers";
import { getMouseCoordinates } from "../utils/canvasHelpers";
import { debounce } from "../utils/debounce";
import { isInsideBox, generateUniqueId } from "../utils/droneNestUtils";
import { drawDroneNests, getLabelCharacter } from "../utils/drawUtils";
import useSmoothCenterOnItem from "./useSmoothCenterOnItem";
import { updateCanvasCursor } from "../utils/canvasCursorUtils";
import { applyZoomToCanvas } from "../utils/canvasZoomUtils";
import { useDialog } from "../../../../../../context";

// Constants
const boxSize = 24;
const fontSize = 15;
const letterColor = "#ffffff";
const backgroundColor = "#144FE1";
const borderColor = "#ffffff";
const borderRadius = 4;
const fallbackImageUrl = `http://54.158.123.107:8080/v1/assets/floor-plans/floor_plan_1750250152_8855.webp`;

const useCanvasInteraction = ({ droneNestClicked }) => {
  const { setActiveCampus } = useActiveCampusContext();
  const { showInfo } = useDialog();
  const { activeDroneNest, setActiveDroneNest } = useActiveDroneNestContext();
  const { activeFloor, action, setAction, activeFloorDroneNest } =
    useActiveFloorContext();

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [start, setStart] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredBoxId, setHoveredBoxId] = useState(null);

  const { scale, image, type, isDirty, isCenter, showOnMap } = action;

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const prevScaleRef = useRef(scale);

  // Auto center logic
  useSmoothCenterOnItem({
    targetId: showOnMap,
    isCenter,
    setAction,
    image,
    items: activeFloorDroneNest,
    scale,
    canvasRef,
    containerRef,
    setPosition,
  });

  // Mouse Events
  const handleMouseDown = (e) => {
    if (type !== "move") return;
    setIsDragging(true);
    setStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const rawMouseMove = useCallback(
    (e) => {
      const canvas = canvasRef.current;
      if (!canvas || scale <= 0) return;

      const { x, y } = getMouseCoordinates(e, canvas, scale);

      if (isDragging) {
        setPosition({ x: e.clientX - start.x, y: e.clientY - start.y });
      }

      // if (type !== "edit") return;

      const hovered = activeFloorDroneNest.find(({ longitude, latitude }) =>
        isInsideBox(x, y, longitude, latitude, boxSize),
      );
      setHoveredBoxId(hovered?.id ?? null);
    },
    [activeFloorDroneNest, isDragging, start, scale, type],
  );

  const handleMouseMove = useMemo(
    () => debounce(rawMouseMove, 10),
    [rawMouseMove],
  );
  const handleMouseUp = () => setIsDragging(false);

  // Apply zoom animation
  const scaleCenter = useCallback(
    (canvas) => {
      applyZoomToCanvas({
        canvas,
        container: containerRef.current,
        scale,
        prevScaleRef,
        position,
        setPosition,
      });
    },
    [position, scale],
  );

  // Apply cursor styling
  useEffect(() => {
    updateCanvasCursor({
      canvas: canvasRef.current,
      hoveredBoxId,
      isDragging,
      type,
    });
  }, [hoveredBoxId, isDragging, type]);

  // Setup listeners
  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      handleMouseMove.cancel?.();
    };
  }, [handleMouseMove]);

  // Redraw Canvas
  const draw = useCallback(
    (canvas, img) => {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.save();
      setTimeout(() => {
        ctx.drawImage(img, 0, 0);

        drawDroneNests(ctx, activeFloorDroneNest, {
          boxSize,
          borderRadius,
          fontSize,
          backgroundColor,
          borderColor,
          letterColor,
          showOnMap,
          hoveredBoxId,
        });
      }, 0);
      scaleCenter(canvas);
    },
    [activeFloorDroneNest, hoveredBoxId, scale, scaleCenter, showOnMap],
  );

  const callDraw = () => {
    if (!image) return;
    const canvas = canvasRef.current;
    canvas.width = image.width * scale;
    canvas.height = image.height * scale;
    draw(canvas, image);
  };

  // Canvas click handler
  const handleClick = (e) => {
    // if (CheckValue(type) && type !== "add") return;

    const canvas = canvasRef.current;
    const { x, y } = getMouseCoordinates(e, canvas, scale);

    let drone_nest = {};

    if (type === "add") {
      if (isDirty) {
        showInfo(staticVar.info);
        return;
      }
      drone_nest = setDroneNest({
        longitude: x,
        latitude: y,
        floor_id: activeFloor?.id,
        campus_id: activeFloor?.campus_id,
        ID: `local_${generateUniqueId()}`,
        display_id: getLabelCharacter(activeFloorDroneNest?.length || 0),
      });
      setActiveCampus((prev) => ({
        ...prev,
        drone_nests: [...prev.drone_nests, { ...drone_nest }],
      }));
    } else {
      const droneNestIndex = activeFloorDroneNest.findIndex(
        ({ longitude, latitude }) =>
          isInsideBox(x, y, longitude, latitude, boxSize),
      );
      if (droneNestIndex < 0) return;
      drone_nest = { ...activeFloorDroneNest[droneNestIndex] };
      if (!drone_nest || drone_nest?.id === activeDroneNest?.id) return;

      if (!drone_nest.display_id) {
        drone_nest.display_id = getLabelCharacter(droneNestIndex);
      }
      if (isDirty) {
        showInfo(staticVar.info);
        return;
      }
    }
    if (!!droneNestClicked) {
      droneNestClicked({ ...drone_nest });
    } else {
      setActiveDroneNest({ ...drone_nest });
    }
    setHoveredBoxId(null);
    let temp = { showOnMap: drone_nest.id, isDirty: type === "add" };
    if (type === "add") {
      temp["type"] = null;
    }
    setAction((prev) => ({
      ...prev,
      ...temp,
    }));
  };

  // Load image when active floor changes
  useEffect(() => {
    if (CheckValue(activeFloor?.floor_plan_url)) {
      const img = new Image();
      let isMounted = true;
      img.crossOrigin = "Anonymous";

      img.onload = () => {
        if (isMounted) {
          setAction((prev) => ({
            ...prev,
            image: img,
            isCenter: !CheckValue(prev.showOnMap),
          }));
        }
      };

      img.onerror = () =>
        console.error("Image load failed:", activeFloor?.floor_plan_url);
      img.src = activeFloor?.floor_plan_url || fallbackImageUrl;

      return () => {
        isMounted = false;
      };
    }
  }, [activeFloor?.floor_plan_url]);

  // Trigger draw
  useEffect(() => {
    if (image) callDraw();
  }, [image, scale, hoveredBoxId, showOnMap, activeFloorDroneNest]);

  return {
    canvasRef,
    containerRef,
    position,
    isDragging,
    hoveredBoxId,
    handleMouseDown,
    handleClick,
    image,
    centerCanvas: () => setAction((prev) => ({ ...prev, isCenter: true })),
  };
};

export default useCanvasInteraction;
